import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { currentStage, nextAction, stagesFor, stopReason, type WorkflowFacts } from '@/pages/workflow';

/**
 * Where you are, and the one thing to do next.
 *
 * The same six stages render on every workflow surface, from the same facts,
 * so moving between the bucket page and the packaging screen never changes the
 * metaphor or loses the operator's place. The current stage carries the only
 * primary action on the page; a stage that cannot complete is marked as such
 * where an operator would otherwise try it, rather than looking merely undone.
 */

/**
 * Blocked and failed were drawn as "not started yet".
 *
 * All three of ahead, blocked and unknown shared one grey, so a stage that
 * had been reached and could not complete looked exactly like one nobody had
 * got to, which is the difference between something to act on and something
 * to ignore.
 */
/**
 * The banner names the one action, and on the page where that action is
 * actually performed it should be the thing that performs it.
 *
 * It was a link, always, so on the bucket page it pointed at the URL already
 * loaded: pressing the single most prominent control in the product did
 * nothing, and the real button sat in the ladder below wearing the same words.
 * Two identical offers, one of them inert. A page that owns the action hands
 * it in, and then it is the only offer on screen.
 */
export interface RailAct {
  /** Runs the stage the banner is naming. */
  onClick: () => void;
  /** Whether it is running right now. */
  busy: boolean;
  /** What is happening, shown under the banner while it runs. */
  note?: React.ReactNode;
}

export function WorkflowRail({
  facts,
  bucketId,
  act,
}: {
  facts: WorkflowFacts;
  bucketId: string | null;
  act?: RailAct | null;
}) {
  const stages = stagesFor(facts);
  const here = currentStage(stages);
  const action = nextAction(facts, bucketId);
  const stopped = stopReason(facts);
  /**
   * A button that goes where you already are is not an action.
   *
   * The packaging page draws this rail, and the rail's action for a
   * part-built package is the packaging page: the most prominent control in
   * the product sat at the top of the very screen it pointed at, and pressing
   * it did nothing. An anchor is different - it moves you to the control - so
   * only a bare same-page route is dropped.
   */
  /**
   * Every stage finished, which the banner had no word for.
   *
   * `currentStage` falls back to the last stage when nothing is outstanding,
   * so a delivered package read "You are here: Hand it over. Finished when
   * copied into the buyer's own bucket" - present tense position, future
   * tense completion, about work that had already completed.
   */
  const allDone = stages.every((s) => s.status === 'done');
  const path = useLocation().pathname;
  const inert = Boolean(action && !action.to.includes('#') && action.to === path && !act);

  return (
    <section className="mb-3 overflow-hidden rounded-md border border-line bg-panel">
      {/* The six chips lived here, and the sidebar draws the same six as its
          body now. Two spines on one screen is one too many: the rail
          navigates, and this says where you are and what to do about it. */}
      <div className="flex flex-wrap items-center gap-3 bg-acc-soft px-4 py-3">
        <span className="min-w-0 flex-1">
          <span className="mono text-caption uppercase tracking-wider text-acc-2">
            {allDone
              ? 'Finished'
              : here.status === 'running'
                ? 'Working'
                : here.status === 'unknown'
                  ? 'Reading'
                  : 'You are here'}
          </span>{' '}
          <span className="text-small text-ink">{allDone ? 'This bucket is handed over.' : `${here.title}.`}</span>{' '}
          <span className="text-small text-ink-2">
            {(allDone
              ? 'Every stage is complete and the copy was verified into the buyer’s bucket. Nothing here needs you.'
              : undefined) ??
              stopped ??
              (action?.because && inert
                ? `${action.because} The step below is where it happens.`
                : action?.because) ??
              (here.status === 'running'
                ? 'The backend is working on this. Nothing is needed from you.'
                : here.status === 'unknown'
                  ? 'Reading this bucket’s state before saying anything about it.'
                  : `Finished when ${here.done}.`)}
          </span>
        </span>
        {act ? (
          <button
            type="button"
            onClick={act.onClick}
            disabled={act.busy}
            className="flex flex-none items-center gap-1.5 rounded border border-acc bg-acc px-3 py-1.5 text-small font-medium text-acc-ink transition-colors duration-fast hover:bg-acc-hov disabled:opacity-70"
          >
            {act.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
            {act.busy ? 'Working…' : (action?.label ?? 'Continue')}
          </button>
        ) : action && !inert ? (
          <Link
            to={action.to}
            /* `text-app` is not a token, so it generated no rule at all and
               the label inherited the page's light ink onto a gold fill:
               white-on-gold, about 1.9:1, on the single most important
               control in the product. `acc-ink` is the token that exists for
               ink on an accent fill, and it is near-black on dark gold and
               white on light gold, so it is correct in both themes. */
            className="flex flex-none items-center gap-1.5 rounded border border-acc bg-acc px-3 py-1.5 text-small font-medium text-acc-ink transition-colors duration-fast hover:bg-acc-hov"
          >
            {action.label}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        ) : null}
      </div>

      {/* What is running, next to the control that started it. It used to sit
          in the ladder below, so an operator pressed a button at the top of the
          page and the only sign anything had happened was out of view. */}
      {act?.note && <div className="border-t border-line bg-acc-soft px-4 pb-3">{act.note}</div>}
    </section>
  );
}
